export const GOAL_HISTORY_MAX = 20;

export function normalizeGoalText(goal) {
  return String(goal || '').trim().slice(0, 500);
}

export function goalKey(goal) {
  return normalizeGoalText(goal).toLowerCase();
}

export function normalizeAchievementRate(value) {
  if (!Number.isFinite(Number(value))) return null;
  return Math.max(0, Math.min(100, Math.round(Number(value))));
}

export function normalizeStatus(status) {
  return status === 'finished' ? 'finished' : 'in_progress';
}

function sanitizeCachedRecommendations(recs) {
  if (!recs || typeof recs !== 'object') return null;
  return recs;
}

/**
 * Upsert a goalHistory entry by goal text (case-insensitive).
 * Preserves finished unless overwriteStatus or new status is finished.
 */
export function upsertGoalHistoryEntry(existing, entry, { overwriteStatus = false } = {}) {
  const goal = normalizeGoalText(entry?.goal);
  if (!goal) {
    return Array.isArray(existing) ? existing.slice(0, GOAL_HISTORY_MAX) : [];
  }

  const list = Array.isArray(existing) ? existing : [];
  const key = goalKey(goal);
  const prev = list.find(item => goalKey(item?.goal) === key);
  const nextStatus = normalizeStatus(entry?.status);
  const status =
    !overwriteStatus && prev?.status === 'finished' && nextStatus !== 'finished'
      ? 'finished'
      : nextStatus;

  const nextEntry = {
    goal,
    at: entry?.at || new Date().toISOString(),
    status,
    achievementRate:
      entry?.achievementRate !== undefined
        ? normalizeAchievementRate(entry.achievementRate)
        : (prev?.achievementRate ?? null),
    progressSummary:
      entry?.progressSummary !== undefined
        ? String(entry.progressSummary || '').slice(0, 240)
        : String(prev?.progressSummary || '').slice(0, 240),
    cachedRecommendations:
      entry?.cachedRecommendations !== undefined
        ? sanitizeCachedRecommendations(entry.cachedRecommendations)
        : (prev?.cachedRecommendations ?? null),
    // Keep the original step actor across select / soft-progress snapshots.
    createdBy:
      entry?.createdBy !== undefined
        ? entry.createdBy
        : (prev?.createdBy ?? null),
    createdByUsername:
      entry?.createdByUsername !== undefined
        ? String(entry.createdByUsername || '')
        : String(prev?.createdByUsername || ''),
    createdByProfileImageUrl:
      entry?.createdByProfileImageUrl !== undefined
        ? String(entry.createdByProfileImageUrl || '')
        : String(prev?.createdByProfileImageUrl || ''),
  };

  const withoutDup = list.filter(item => goalKey(item?.goal) !== key);
  return [nextEntry, ...withoutDup].slice(0, GOAL_HISTORY_MAX);
}

export function buildSnapshotFromRecommendations(goal, recommendations, status = 'in_progress') {
  const recs = recommendations && typeof recommendations === 'object' ? recommendations : null;
  return {
    goal: normalizeGoalText(goal),
    status: normalizeStatus(status),
    achievementRate: normalizeAchievementRate(recs?.achievementRate),
    progressSummary: String(recs?.progressSummary || '').slice(0, 240),
    cachedRecommendations: sanitizeCachedRecommendations(recs),
    at: new Date().toISOString(),
  };
}

/** True when a canvas object filled or partially filled a gap on this step. */
export function hasCanvasFillProgress(recs) {
  const up = recs?.userProgress;
  if (!up || typeof up !== 'object') return false;
  return Boolean(
    (Array.isArray(up.doneFills) && up.doneFills.length)
    || (Array.isArray(up.partialFills) && up.partialFills.length)
    || (Array.isArray(up.partialBlockerKeys) && up.partialBlockerKeys.length)
  );
}

/**
 * True when the step is in-progress for refresh / history keep.
 * Only canvas fill or partial fill counts — not search/ask, ticks, or not-interested.
 */
export function hasUserCoachProgress(recs) {
  return hasCanvasFillProgress(recs);
}

/** Stable key for a blocker / missing-part row (matches BoardAiToolbar). */
export function blockerDoneKey(blocker, index = 0) {
  if (typeof blocker === 'string') {
    const title = String(blocker || '').trim();
    return title || `blocker:${index}`;
  }
  const title = String(blocker?.title || '').trim();
  return title || `blocker:${index}`;
}

/**
 * True when every listed gap is closed via Solved or Not interested (and every
 * validation probe is marked done). Empty gap lists never auto-finish.
 */
export function areGoalStepGapsResolved(recommendations, {
  doneBlockerKeys = null,
  notInterestedKeys = null,
  doneValidationKeys = null,
} = {}) {
  const recs = recommendations && typeof recommendations === 'object' ? recommendations : null;
  if (!recs) return false;
  const up = recs.userProgress && typeof recs.userProgress === 'object' ? recs.userProgress : {};
  const done = new Set(
    (Array.isArray(doneBlockerKeys) ? doneBlockerKeys : (up.doneBlockerKeys || []))
      .map(v => String(v || '').trim())
      .filter(Boolean)
  );
  const notInterest = new Set(
    (Array.isArray(notInterestedKeys) ? notInterestedKeys : (up.notInterestedKeys || []))
      .map(v => String(v || '').trim())
      .filter(Boolean)
  );
  const doneValidation = new Set(
    (Array.isArray(doneValidationKeys) ? doneValidationKeys : (up.doneValidationKeys || []))
      .map(v => String(v || '').trim())
      .filter(Boolean)
  );

  const blockers = Array.isArray(recs.blockers) && recs.blockers.length
    ? recs.blockers
    : (Array.isArray(recs.gaps) ? recs.gaps : []);
  const validations = Array.isArray(recs.validationQueue) ? recs.validationQueue : [];
  if (!blockers.length && !validations.length) return false;

  for (let index = 0; index < blockers.length; index += 1) {
    const key = blockerDoneKey(blockers[index], index);
    if (!done.has(key) && !notInterest.has(key)) return false;
  }
  for (const item of validations) {
    const key = String(item?.key || '').trim();
    if (!key) continue;
    if (!doneValidation.has(key)) return false;
  }
  return true;
}

/** finished when all gaps are solved / not-interested; otherwise in_progress. */
export function resolveGoalStepStatus(recommendations, progressKeys = {}) {
  return areGoalStepGapsResolved(recommendations, progressKeys) ? 'finished' : 'in_progress';
}

/** True when the user has real progress on this goal (keep across context refresh). */
export function isProgressedGoalHistoryEntry(entry) {
  if (!entry) return false;
  if (normalizeStatus(entry.status) === 'finished') return true;
  // Only canvas fill / partial fill counts — selecting a step or ticking a gap
  // must not keep the row as in-progress.
  if (hasUserCoachProgress(entry.cachedRecommendations)) return true;
  return false;
}

export function removeGoalHistoryEntry(existing, goal) {
  const key = goalKey(goal);
  if (!key) return Array.isArray(existing) ? existing.slice(0, GOAL_HISTORY_MAX) : [];
  return (Array.isArray(existing) ? existing : [])
    .filter(item => goalKey(item?.goal) !== key)
    .slice(0, GOAL_HISTORY_MAX);
}

/**
 * Drop non-progressed history stubs so a context refresh can show fresh stepCandidates.
 * Always keeps the active current goal and any progressed/finished entries.
 */
export function pruneUnprogressedGoalHistory(existing, { keepGoal = '' } = {}) {
  const keepKey = goalKey(keepGoal);
  return (Array.isArray(existing) ? existing : [])
    .filter(item => {
      if (keepKey && goalKey(item?.goal) === keepKey) return true;
      return isProgressedGoalHistoryEntry(item);
    })
    .slice(0, GOAL_HISTORY_MAX);
}
